'use client';
import * as Sentry from '@sentry/nextjs';

import {
  ScrollStepContentProps,
  StepContentServiceProps,
} from '@/common/components/SalesFlow/StepsContent/StepContent.interfaces';

import { useExtensionFlow } from '@/widgets/ExtensionFlow/context/ExtensionFlowContext';
import { useStepContentLogic } from '@/common/components/SalesFlow/StepsContent/hooks/useStepContentLogic';
import { useScrollStepContentLogic } from '@/common/components/SalesFlow/StepsContent/hooks/useScrollStepContentLogic';
import { getExtensionStepMap } from '@/common/components/SalesFlow/StepsContent/utils/getStepsMap';
import { getStepTitle } from '@/common/components/SalesFlow/StepsContent/utils/getStepTitle';
import { useUpdateQueryParams } from '@/widgets/BecomeAMember/hooks/useUpdateQueryParams';
import { useCallback, useEffect, useRef, useState } from 'react';
import { CookieKeys, Languages, UrlParamKeys } from '@/common/utils/constants';
import { getCookie } from '@/common/utils/cookies';
import { getSavedPaymentInfo } from '@/common/api/payment-session';
import { usePaymentSession } from '@/widgets/ExtensionFlow/hooks/usePaymentSession';
import { handleMembershipExtension } from './components/ExtensionTermsAndCondition/ExtensionTermsAndCondition.service';
import { Club } from '@/generated/contentfulTypes';
import { Maybe } from 'graphql/jsutils/Maybe';
import { FlowType } from '@/common/api/interfaces';
import { useTermSignupPreview } from '@/common/hooks/useTermSignupPreview';
import { arePaymentTokensValid, buildPaymentSessionKey, getPaymentAmount, getSignUpAmount, isSavedSessionKeyEqual } from '@/common/utils/paymentUtils';

export const useExtensionStepContent = (
  props: StepContentServiceProps
) => {
  const extensionFlow = useExtensionFlow();

  const stepItems = props.stepsCollection?.items || [];
  const stepsMap = getExtensionStepMap(stepItems)

  const stepsLogic = useStepContentLogic(props, {
    setActiveStep: extensionFlow.setActiveStep,
    setPaidModules: extensionFlow.setPaidModules,
    setFreeModules: extensionFlow.setFreeModules,
    setStudioTermData: extensionFlow.setStudioTermData,
    setVoucherData: extensionFlow.setVoucherData,
    setHasAdditionalModules: extensionFlow.setHasAdditionalModules,
    setAllowedPaymentMethods: extensionFlow.setAllowedPaymentMethods,
    studioTermData: extensionFlow.studioTermData
  });

  const stepTitles = {
    membershipType: getStepTitle(stepsMap.membershipType, stepItems),
    accessLevel: getStepTitle(stepsMap.accessLevel, stepItems),
    terms: getStepTitle(stepsMap.terms, stepItems),
    modules: stepsMap.extras
      ? getStepTitle(stepsMap.extras, stepItems)
      : undefined,
    verification: getStepTitle(stepsMap.verification, stepItems),
    payment: stepsMap.payment ? getStepTitle(stepsMap.payment, stepItems) : undefined,
  }

  return { stepTitles, stepsMap, stepItems, ...stepsLogic };
};

export const useExtensionScrollStepContent = (
  props: ScrollStepContentProps
) => {
  const {
    isManuallyScrolling,
    setIsManuallyScrolling,
    setActiveStep,
  } = useExtensionFlow();

  const scrollData = useScrollStepContentLogic(props, {
    isManuallyScrolling,
    setIsManuallyScrolling,
    setActiveStep,
  })

  return {
    isManuallyScrolling,
    ...scrollData,
  };
};

export const useExtensionPaymentFlow = ({ club, lang }: { club: Maybe<Club>, lang: Languages }) => {
  const { searchParams, updateUrl } = useUpdateQueryParams();
  const [isProcessingSignUp, setIsProcessingSignUp] = useState(false);
  const isPaymentSkipped = Boolean(searchParams.get(UrlParamKeys.SkipPayment)) || getCookie(CookieKeys.EXTENSION_HAS_PAYMENT_METHOD) === 'true';

  const selectedPaymentMethod = searchParams.get(UrlParamKeys.PaymentMethod);
  const rateBundleTermIdSelected = searchParams.get(UrlParamKeys.RateBundleTermId);
  const hasSavedPaymentInfo = getCookie(CookieKeys.EXTENSION_PAYMENT_IS_SAVED) === "true";
  const parsedPaymentInfoCookie = getSavedPaymentInfo(FlowType.EXTENSION);
  const termsValidatorRef = useRef<(() => Promise<boolean>) | null>(null);

  const {
    translations,
    setIsPaymentWidgetVisible,
    dataLayerData,
    customerExtensionEndDate,
    cancelationReasonId,
    additionalInformationId,
    additionalInformationValue,
    studioTermData
  } = useExtensionFlow();

  const startDate = studioTermData?.defaultContractStartDate ?? '';
  const preuseDate = studioTermData?.contractStartDateOfUse ?? '';

  const { data: previewData } = useTermSignupPreview({
    termId: Number(studioTermData?.termId) ?? 0,
    studioId: studioTermData?.studioId ?? '',
    startDate,
    preuseDate,
    voucherCode: studioTermData?.voucherCode?.voucherCode ?? '',
    paidModuleIds: [],
    freeModuleIds: [],
    lang,
    isPreviewEnabled: !!studioTermData && !!club?.chargeProrataAtSignup
  });

  const amount = getPaymentAmount(previewData?.payment_preview?.due_on_signing_amount?.amount);
  const signUpAmount = getSignUpAmount(club, amount);

  const paymentSessionKey = buildPaymentSessionKey(
    rateBundleTermIdSelected,
    selectedPaymentMethod,
    signUpAmount
  );

  const { triggerPaymentSession, isPaymentSessionLoading, isPaymentSessionFailed } = usePaymentSession({
    translations,
    searchParams,
    club,
    lang,
    updateUrl,
    setIsPaymentWidgetVisible,
    paymentSessionKey,
    amount
  });

  const validateTerms = useCallback(async () => {
    if (termsValidatorRef.current) {
      return await termsValidatorRef.current();
    }
    return true;
  }, []);

  const onRegisterValidator = useCallback((validator: () => Promise<boolean>) => termsValidatorRef.current = validator, [])

  useEffect(() => {
    if (isPaymentSkipped || hasSavedPaymentInfo || !paymentSessionKey) return;

    const areTokensValid = arePaymentTokensValid(parsedPaymentInfoCookie);
    const isSavedContextMatch =
      signUpAmount != null &&
      isSavedSessionKeyEqual(parsedPaymentInfoCookie, paymentSessionKey);

    if (areTokensValid && isSavedContextMatch) return;

    triggerPaymentSession();
  }, [
    isPaymentSkipped,
    paymentSessionKey,
    triggerPaymentSession,
    hasSavedPaymentInfo,
    parsedPaymentInfoCookie,
    signUpAmount
  ]);

  const handleWidgetSuccess = useCallback(async () => {
    try {
      if (termsValidatorRef.current) {
        const isTermsValid = await termsValidatorRef.current();
        if (!isTermsValid) {
          setIsProcessingSignUp(false);
          return;
        }
      }

      setIsProcessingSignUp(true);
      const onError = () => {
        setIsPaymentWidgetVisible(false);
        setIsProcessingSignUp(false);
      };

      const response = await handleMembershipExtension({
        club,
        dataLayerData,
        customerExtensionEndDate,
        cancelationReasonId,
        onError,
        searchParams,
        lang,
        additionalInformationId,
        additionalInformationValue
      });

      if (!response) {
        setIsProcessingSignUp(false);
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error('Widget success failed', error);
      setIsProcessingSignUp(false);
    }
  }, [club, dataLayerData, customerExtensionEndDate, cancelationReasonId, searchParams, additionalInformationId, additionalInformationValue, lang, setIsPaymentWidgetVisible]);

  return { isPaymentSkipped, onRegisterValidator, handleWidgetSuccess, isProcessingSignUp, hasSavedPaymentInfo, isPaymentSessionFailed, isPaymentSessionLoading, validateTerms, setIsProcessingSignUp }
}